/**
 * useCaseFormatters Composable
 *
 * Centralizes formatting functions used across case management components.
 * Extracted from DocumentationValidationModal.vue, CaseTable.vue, and CaseDetail.vue
 * to follow DRY principle and ensure consistency.
 *
 * Following patterns from PATTERNS_FROM_BASKET_BASE.md
 */

import { PRICING } from '@/utils/constants/pricing.js'
import { LEGITIMATION_LABELS } from '@/constants/legitimation'

/**
 * Composable for case-related formatting functions
 * @returns {Object} Formatting functions for case management
 */
export function useCaseFormatters() {
  /**
   * Format service tier to human-readable label with price
   * @param {string} tier - Service tier identifier (olvido, olvido_postmortem, testamento)
   * @returns {string} Formatted tier label with price
   */
  const formatServiceTier = (tier) => {
    const tiers = {
      olvido: `${PRICING.OLVIDO.name} (${PRICING.OLVIDO.price}€)`,
      olvido_postmortem: `${PRICING.OLVIDO_POSTMORTEM.name} (${PRICING.OLVIDO_POSTMORTEM.price}€)`,
      testamento: `${PRICING.TESTAMENTO.name} (${PRICING.TESTAMENTO.price}€)`
    }
    return tiers[tier] || tier || '-'
  }

  /**
   * Format documentation status to Spanish label
   * @param {string} status - Documentation status (pending, approved, rejected)
   * @returns {string} Spanish label for the status
   */
  const formatDocStatus = (status) => {
    const labels = {
      pending: 'Pendiente',
      approved: 'Aprobada',
      rejected: 'Rechazada'
    }
    return labels[status] || 'Pendiente'
  }

  /**
   * Get Tailwind CSS classes for documentation status badge
   * @param {string} status - Documentation status (pending, approved, rejected)
   * @returns {string} Tailwind CSS class string for badge styling
   */
  const getDocStatusBadgeClass = (status) => {
    const classes = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    }
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classes[status] || classes.pending}`
  }

  /**
   * Format date with time for case management views
   * Uses Spanish locale with day/month/year and hour:minute format
   * @param {string|Date} dateString - Date to format
   * @returns {string} Formatted date string (e.g., "16/01/26, 14:30")
   */
  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Format document type to Spanish label.
   *
   * Keys use two naming conventions because they come from different Mongoose
   * schemas: user-uploaded documents use snake_case (Document.js) while
   * admin/system-generated documents use kebab-case (Case.js embedded docs).
   * Changing this requires a database migration.
   *
   * @param {string} type - Document type identifier
   * @returns {string} Spanish label for document type
   */
  const formatDocType = (type) => {
    const types = {
      // User-uploaded document types (snake_case - Document.js schema)
      dni: 'DNI/NIE',
      death_certificate: 'Certificado de defunción',
      authorization: 'Autorización',
      power_of_attorney: 'Poder notarial',
      other: 'Otro',
      legal_document: 'Documento legal',
      identity_document: 'Documento de identidad',
      passport: 'Pasaporte',
      birth_certificate: 'Certificado de nacimiento',
      court_filing: 'Escrito judicial',
      notarized_document: 'Documento notarizado',
      generated_letter: 'Carta generada',
      platform_response: 'Respuesta de plataforma',
      notary_document: 'Documento notarial',
      additional_document: 'Documento adicional',
      authorization_signed: 'Autorización firmada',

      // Admin/system-generated document types (kebab-case - Case.js schema)
      'legal-request': 'Solicitud legal',
      'response-letter': 'Carta de respuesta',
      'legal-notice': 'Notificación legal',
      'compliance-certificate': 'Certificado de cumplimiento',
      'court-document': 'Documento judicial',
      'administrative-resolution': 'Resolución administrativa',
      'client-communication': 'Comunicación con cliente',
      'evidence-document': 'Documento de evidencia',
      'final-report': 'Informe final'
    }
    return types[type] || type || 'Documento'
  }

  /**
   * Format individual document item status
   * @param {string} status - Document status (pending, verified, rejected)
   * @returns {string} Spanish label for the status
   */
  const formatDocItemStatus = (status) => {
    const labels = {
      pending: 'Pendiente',
      verified: 'Verificado',
      rejected: 'Rechazado'
    }
    return labels[status] || 'Pendiente'
  }

  /**
   * Get Tailwind CSS classes for individual document item status
   * @param {string} status - Document status (pending, verified, rejected)
   * @returns {string} Tailwind CSS class string
   */
  const getDocItemStatusClass = (status) => {
    const classes = {
      pending: 'bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full',
      verified: 'bg-green-100 text-green-700 px-2 py-0.5 rounded-full',
      rejected: 'bg-red-100 text-red-700 px-2 py-0.5 rounded-full'
    }
    return classes[status] || classes.pending
  }

  /**
   * Format service type array to comma-separated labels
   * @param {Array} services - Array of service type identifiers
   * @returns {string} Comma-separated Spanish labels
   */
  const formatServiceType = (services) => {
    if (!services || !services.length) return 'Sin servicios'

    const types = {
      'derecho-olvido': 'Derecho al Olvido',
      'derecho-olvido-postmortem': 'Derecho al Olvido Post-Mortem',
      'testamento-digital': 'Testamento Digital'
    }

    return services.map(s => types[s] || s).join(', ')
  }

  /**
   * Check if a case is a Post-Mortem type
   * @param {Object} case_ - Case object
   * @returns {boolean} True if the case is Post-Mortem
   */
  const isPostMortem = (case_) =>
    case_.serviceTier === 'olvido_postmortem' || case_.type === 'olvido_postmortem'

  /**
   * Check if a case is a Testamento Digital type
   * @param {Object} case_ - Case object
   * @returns {boolean} True if the case is Testamento Digital
   */
  const isTestamento = (case_) =>
    case_?.serviceTier === 'testamento' || case_?.type === 'testamento'

  /**
   * Format legal capacity (legalCapacity) to human-readable Spanish label
   * @param {string} capacity - Legitimation type identifier
   * @returns {string} Spanish label for the legal capacity
   */
  const formatLegalCapacity = (capacity) =>
    LEGITIMATION_LABELS[capacity] || capacity || '-'

  /**
   * Calculate days since case was opened
   * @param {string|Date} createdAt - Case creation date
   * @returns {number} Number of days since creation
   */
  const getDaysOpen = (createdAt) => {
    const created = new Date(createdAt)
    const now = new Date()
    const diffTime = Math.abs(now - created)
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  /**
   * Get human-readable case type label
   * @param {string} type - Case type identifier (olvido, olvido_postmortem, testamento)
   * @returns {string} Spanish label for case type
   */
  const getCaseTypeLabel = (type) => {
    const labels = {
      olvido: 'Derecho al Olvido',
      olvido_postmortem: 'Derecho al Olvido Post-Mortem',
      testamento: 'Testamento Digital'
    }
    return labels[type] || type || '-'
  }

  /**
   * Check if a case involves representation (acting on behalf of another person)
   * @param {Object} case_ - Case object
   * @returns {boolean} True if the case has representation
   */
  const isRepresentation = (case_) =>
    case_?.representedPerson?.isRepresented === true

  /**
   * Get the name of the represented person
   * @param {Object} case_ - Case object
   * @returns {string} Name of represented person or '-'
   */
  const getRepresentedPersonName = (case_) =>
    case_?.representedPerson?.name || '-'

  return {
    formatServiceTier,
    formatDocStatus,
    getDocStatusBadgeClass,
    formatDateTime,
    formatDocType,
    formatDocItemStatus,
    getDocItemStatusClass,
    formatServiceType,
    getDaysOpen,
    getCaseTypeLabel,
    isPostMortem,
    isTestamento,
    formatLegalCapacity,
    isRepresentation,
    getRepresentedPersonName
  }
}

export default useCaseFormatters
